<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import WebinarModals from '@/ui-kit/webinars/Modals.vue'
import WebinarCalendar from '@/ui-kit/webinars/Calendar.vue'
import WebinarToolbarClock from '@/ui-kit/webinars/ToolbarClock.vue'
import RequiresAuthModal from '@/plugins/RequiresAuth/RequiresAuthModal.vue'

type ViewMode = 'day' | 'week' | 'month' | 'list'
type WebinarModalName =
  | 'view'
  | 'purchase'
  | 'purchase-video'
  | 'delete'
  | 'edit-link'
  | 'cancel-participation'
  | 'add-review'
  | 'cancel'
  | 'upload'
  | 'finish'
  | 'participants'
  | 'add-question'
  | 'files'
  | 'message'

interface AccessCategory {
  code: string
  name: string
}

interface AccessSeminar {
  id: number
  name: string
  url: string
}

interface PageUrls {
  [key: string]: string
  create: string
  index: string
  info: string
  add: string
  edit: string
  like: string
  purchase: string
  refund: string
  getReviews: string
  upsertReview: string
  upsertResponse: string
  cancel: string
  uploadVideo: string
  searchTeachers: string
  balanceTopup: string
  finishWebinar: string
  purchaseVideo: string
}

interface PageContext {
  seminars: AccessSeminar[]
  ace_courses: AccessSeminar[]
  categories: AccessCategory[]
  has_teacher_grade: boolean
  urls: PageUrls
}

const ctx = usePageContext<PageContext>()
const { isMobile } = useScreen()

const modalEventId = ref<number | null>(null)
const modal = ref<WebinarModalName | null>(null)
const viewMode = ref<ViewMode>('month')
const calendarKey = ref(0)
const waitForLocationFetch = ref(false)

const pageContext = computed(() => {
  return {
    seminars: ctx.seminars,
    ace_courses: ctx.ace_courses,
    categories: ctx.categories
  }
})

const viewModeIndex = computed({
  get: () => {
    const index = ['week', 'month', 'list'].indexOf(viewMode.value)
    return index === -1 ? 1 : index
  },
  set: (index: number) => {
    const mode = ['week', 'month', 'list'][index] as ViewMode | undefined
    viewMode.value = mode || 'month'
  }
})

function refresh() {
  calendarKey.value += 1
}

function openModal(modalName: WebinarModalName, webinarId: number) {
  modalEventId.value = webinarId
  modal.value = modalName
}

function getWebinarIdFromLocation(): number | null {
  const queryId = Number(new URLSearchParams(window.location.search).get('webinar_id'))
  if (queryId > 0) return queryId

  const match = window.location.hash.match(/^#webinar(\d+)$/)
  if (!match) return null

  return Number(match[1])
}

function onLocationChange() {
  const webinarId = getWebinarIdFromLocation()
  if (!webinarId) return

  if (modal.value === 'view' && modalEventId.value === webinarId) return

  modalEventId.value = webinarId
  waitForLocationFetch.value = true
}

function onModalFetchSuccess() {
  if (!waitForLocationFetch.value) return
  waitForLocationFetch.value = false
  modal.value = 'view'
}

onMounted(() => {
  onLocationChange()
  window.addEventListener('popstate', onLocationChange)
  window.addEventListener('hashchange', onLocationChange)
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', onLocationChange)
  window.removeEventListener('hashchange', onLocationChange)
})
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" />

    <div v-cloak class="content webinars-index">
      <v-notifications position="bottom right" />

      <WebinarModals
        v-model:event-id="modalEventId"
        v-model:current-modal="modal"
        :urls="ctx.urls"
        :page-context="pageContext"
        @refresh="refresh"
        @fetch-success="onModalFetchSuccess"
      />

      <div class="webinars-index__header">
        <h1 class="header d-flex align-center">
          <v-link v-if="isMobile && viewMode === 'list'" mode="primary" class="mr-1" @click="viewMode = 'month'">
            <v-icon size="32">mdi-chevron-left</v-icon>
          </v-link>
          Вебинары
        </h1>

        <div class="webinars-index__toolbar">
          <WebinarToolbarClock />

          <v-buttons-group v-if="!isMobile" v-model="viewModeIndex" mode="inactive">
            <v-button mode="primary">Неделя</v-button>
            <v-button mode="primary">Месяц</v-button>
            <v-button mode="primary">Список</v-button>
          </v-buttons-group>

          <template v-if="ctx.has_teacher_grade">
            <a v-if="isMobile" :href="ctx.urls.create">
              <v-icon class="mr-1 blue-text">mdi-calendar-plus</v-icon>
            </a>
            <v-button v-else mode="primary outlined" :href="ctx.urls.create">
              <v-icon class="mr-1">mdi-calendar-plus</v-icon>
              Создать
            </v-button>
          </template>
        </div>
      </div>

      <WebinarCalendar
        :key="calendarKey"
        :urls="ctx.urls"
        :page-context="pageContext"
        class="webinars-index__calendar"
        :view-mode="viewMode"
        @open-event="id => openModal('view', id)"
        @purchase-event="id => openModal('purchase', id)"
        @purchase-video="id => openModal('purchase-video', id)"
        @update:view-mode="
          mode => {
            viewMode = mode
          }
        "
      />

      <RequiresAuthModal />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/webinars/index.scss' as *;
</style>
